import getBrowser from '@tv2/velour/libraries/utils/browserDetection';
import { Drm, HDCPVersion, WidevineSecurityLevel } from '../../types';

type ExtendedMediaKeys = MediaKeys & {
  getStatusForPolicy: (policyObject: Record<string, string>) => Promise<'usable' | string>;
};

type KeySystem = 'com.widevine.alpha';

export interface MediaKeyConfig {
  keySystem: KeySystem;
  videoContentType: string;
  robustness: WidevineSecurityLevel;
}

export const requestMediaKeyAccess = async (
  config: MediaKeyConfig
): Promise<MediaKeySystemAccess | null> => {
  if (!navigator?.requestMediaKeySystemAccess) {
    return null;
  }

  try {
    return await navigator.requestMediaKeySystemAccess(config.keySystem, [
      {
        initDataTypes: ['cenc'],
        videoCapabilities: [
          {
            contentType: config.videoContentType,
            robustness: config.robustness,
          },
        ],
      },
    ]);
  } catch (error) {
    return null;
  }
};

export const checkHDCP = async (
  supportedDRM: Drm[],
  minHdcpVersion: HDCPVersion = HDCPVersion.HD
): Promise<boolean> => {
  if (supportedDRM[0] !== Drm.WIDEVINE) return true;

  const config: MediaKeyConfig = {
    keySystem: 'com.widevine.alpha',
    videoContentType: 'video/mp4; codecs="avc1.640028"',
    robustness: WidevineSecurityLevel.SW_SECURE_DECODE,
  };

  const mediaKeySystemAccess = await requestMediaKeyAccess(config);
  if (!mediaKeySystemAccess) return false;

  const mediaKeys = await mediaKeySystemAccess.createMediaKeys();
  if (mediaKeys && !('getStatusForPolicy' in (mediaKeys as ExtendedMediaKeys))) {
    return false;
  }

  try {
    const statusForPolicy = await (mediaKeys as ExtendedMediaKeys).getStatusForPolicy({
      minHdcpVersion,
    });

    return statusForPolicy === 'usable';
  } catch (error) {
    return false;
  }
};

export const getHighestSupportedRobustness = async (): Promise<WidevineSecurityLevel> => {
  // For safari we just return HW_SECURE_ALL, as it should ne equivalent to FairPlay's capabilities
  if (getBrowser() === 'safari') {
    return WidevineSecurityLevel.HW_SECURE_ALL;
  }

  const robustnessLevels = [
    WidevineSecurityLevel.HW_SECURE_ALL,
    WidevineSecurityLevel.HW_SECURE_DECODE,
    WidevineSecurityLevel.HW_SECURE_CRYPTO,
    WidevineSecurityLevel.SW_SECURE_DECODE,
    WidevineSecurityLevel.SW_SECURE_CRYPTO,
  ];

  const config: MediaKeyConfig = {
    keySystem: 'com.widevine.alpha',
    videoContentType: 'video/mp4; codecs="avc1.640028"',
    robustness: WidevineSecurityLevel.SW_SECURE_DECODE, // Default value, will be overridden
  };

  const results = await Promise.all(
    robustnessLevels.map(async level => {
      try {
        const mediaKeyConfig = {
          ...config,
          robustness: level,
        };
        const mediaKeySystemAccess = await requestMediaKeyAccess(mediaKeyConfig);
        return mediaKeySystemAccess ? level : null;
      } catch (error) {
        return null;
      }
    })
  );

  const highestSupported = results.find(result => result !== null);
  return highestSupported ?? WidevineSecurityLevel.UNSUPPORTED;
};

export const getHDCPVersion = (drmTypes: Drm[], securityLevel: WidevineSecurityLevel) => {
  if (drmTypes[0] !== Drm.WIDEVINE) {
    return HDCPVersion.UHD;
  }

  switch (securityLevel) {
    case WidevineSecurityLevel.HW_SECURE_ALL:
    case WidevineSecurityLevel.HW_SECURE_DECODE:
      // Hardware security typically indicates support for higher HDCP versions
      return HDCPVersion.UHD; // HDCP 2.2
    case WidevineSecurityLevel.HW_SECURE_CRYPTO:
    case WidevineSecurityLevel.SW_SECURE_DECODE:
      // Software decode level typically supports at least HDCP 1.4
      return HDCPVersion.HD; // HDCP 1.4
    case WidevineSecurityLevel.SW_SECURE_CRYPTO:
    case WidevineSecurityLevel.UNSUPPORTED:
    default:
      // Lower security levels or unsupported
      return HDCPVersion.SD; // HDCP 1.0
  }
};
